import { Phone, Mail, ShieldCheck, Check, X } from "lucide-react";
import Seo from "@/components/Seo";
import VerifyForm from "@/components/forms/VerifyForm";
import { SITE } from "@/lib/site";

const STANDARDS_OK = [
  "Representatives should identify themselves by name.",
  "Representatives should say they are with Maritime Thermal Solutions.",
  "Representatives should explain that we coordinate appointments with partner HVAC companies.",
  "Representatives should leave respectfully if asked.",
];

const STANDARDS_NEVER = [
  "Claim to be from the government, a utility, or a rebate program.",
  "Pressure homeowners into booking an appointment.",
  "Make guaranteed savings claims.",
  "Refuse to leave when asked to.",
];

export default function Verify() {
  return (
    <>
      <Seo
        title="Verify a Representative | Maritime Thermal Solutions"
        description="Verify a Maritime Thermal Solutions representative or contact us about a recent visit, appointment, or concern."
      />

      <section className="border-b border-slate-200 bg-slate-50">
        <div className="container-x py-14 md:py-20">
          <div className="grid items-start gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="eyebrow">Verify a Representative</p>
              <h1 className="mt-3 font-heading text-4xl font-bold leading-tight text-brand-navy sm:text-5xl" data-testid="verify-heading">
                Verify a Maritime Thermal Solutions Representative
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                All Maritime Thermal Solutions representatives are expected to clearly identify
                themselves, explain the purpose of their visit, and conduct themselves respectfully.
                If someone has visited your home and you would like to verify their identity, please
                contact us using the form below or call us directly.
              </p>
            </div>
            <aside className="rounded-lg border border-slate-200 bg-white p-6 lg:col-span-5" data-testid="verify-contact-card">
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-orange/10 px-3 py-1 text-xs font-semibold text-brand-orange">
                <ShieldCheck size={14} aria-hidden="true" />
                Reach us directly
              </div>
              <ul className="mt-4 space-y-3 text-sm">
                <li>
                  <a href={SITE.phoneHref} className="flex items-center gap-3 text-slate-800 hover:text-brand-navy" data-testid="verify-phone-link">
                    <Phone size={18} className="text-brand-navy" aria-hidden="true" />
                    <span className="font-semibold">{SITE.phone}</span>
                  </a>
                </li>
                <li>
                  <a href={SITE.emailHref} className="flex items-center gap-3 text-slate-800 hover:text-brand-navy break-all" data-testid="verify-email-link">
                    <Mail size={18} className="text-brand-navy" aria-hidden="true" />
                    <span className="font-semibold">{SITE.email}</span>
                  </a>
                </li>
              </ul>
              <p className="mt-4 text-xs text-slate-500">
                Available Monday–Friday, 9:00 AM – 5:00 PM. Outside business hours, please leave a message or use the form.
              </p>
            </aside>
          </div>
        </div>
      </section>

      <section className="section-y" data-testid="verify-form-section">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-2xl font-semibold text-slate-900 sm:text-3xl">
              Send us the details
            </h2>
            <p className="mt-2 text-slate-600">
              Share what you can — we'll review the information and follow up to confirm.
            </p>
            <div className="mt-6">
              <VerifyForm />
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h3 className="font-heading text-lg font-semibold text-slate-900">Representative Standards</h3>
              <p className="mt-1 text-sm text-slate-600">What our representatives should always do:</p>
              <ul className="mt-4 space-y-3" data-testid="verify-standards-ok">
                {STANDARDS_OK.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check size={18} className="mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 border-t border-slate-200 pt-5">
                <p className="text-sm font-semibold text-slate-900">What our representatives should never do:</p>
                <ul className="mt-3 space-y-3" data-testid="verify-standards-never">
                  {STANDARDS_NEVER.map((s) => (
                    <li key={s} className="flex items-start gap-3 text-sm text-slate-700">
                      <X size={18} className="mt-0.5 shrink-0 text-rose-500" aria-hidden="true" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
